import { Controller } from "@hotwired/stimulus"
import { getCameraCaptureMode } from "../lib/camera_capture_preference"
import { focusOrNudgeTrack, focusDiagnosticsForTrack } from "../lib/camera_stream"
import { focusPointFromTap, showFocusRing } from "../lib/tap_to_focus"
import { logEvent } from "../field_logger"

// Opens the rear camera with a 3:1 landscape aspect ratio constraint,
// shows a live preview with a crop guide, then captures and injects the
// result into a hidden file input.  Falls back to the native file picker
// if getUserMedia is unavailable or permission is denied.
//
// Native handoff (opt-in, device-detected default -- see
// camera_capture_preference.js): when the tasker's preference is "native",
// open() goes straight to the SAME file-picker fallback used reactively on
// getUserMedia failure below -- no other change to this controller's
// crop/capture logic.
//
// Tap-to-focus (card #4495): honest-gated the same way as the photo camera
// (field_photo_camera_controller.js, card #3488) -- Chrome/Android only,
// feature-detected per-device via MediaTrackCapabilities, never a UA sniff.
// This controller drives its own raw getUserMedia stream rather than the
// CameraStream class, so it calls camera_stream.js's exported
// focusCapabilityForTrack()/applyFocusToTrack() directly against its own
// track, and shares the tap-point maths/ring visual with the photo camera
// via lib/tap_to_focus.js.
export default class extends Controller {
  static targets = ["trigger", "video", "preview", "captureBtn", "retakeBtn", "result", "input", "guide"]

  connect() {
    this.stream = null
    this._focusTapHandler = null
  }

  async open() {
    if (getCameraCaptureMode() === "native") {
      this.inputTarget.click()
      return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.inputTarget.click()
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          aspectRatio: { ideal: 3.0 },
          width:  { ideal: 1920 },
          height: { ideal: 640 }
        }
      })

      this.videoTarget.srcObject = this.stream
      await this.videoTarget.play()

      this.triggerTarget.classList.add("hidden")
      this.videoTarget.classList.remove("hidden")
      this.guideTarget.classList.remove("hidden")
      this.captureBtnTarget.classList.remove("hidden")

      this._syncFocusTap()
    } catch (_e) {
      // Permission denied or not supported — fall back to file picker
      this.inputTarget.click()
    }
  }

  capture() {
    const video = this.videoTarget
    const canvas = document.createElement("canvas")

    // Crop centre of video to exactly 3:1
    const srcW = video.videoWidth
    const srcH = Math.round(srcW / 3)
    const srcY = Math.round((video.videoHeight - srcH) / 2)

    canvas.width  = srcW
    canvas.height = srcH
    canvas.getContext("2d").drawImage(video, 0, srcY, srcW, srcH, 0, 0, srcW, srcH)

    canvas.toBlob(blob => {
      // Inject into file input
      const file = new File([blob], "odometer.jpg", { type: "image/jpeg" })
      const dt = new DataTransfer()
      dt.items.add(file)
      this.inputTarget.files = dt.files

      // Show result preview
      this.resultTarget.src = URL.createObjectURL(blob)
      this.resultTarget.classList.remove("hidden")
      this.retakeBtnTarget.classList.remove("hidden")

      // Hide camera UI
      this.videoTarget.classList.add("hidden")
      this.guideTarget.classList.add("hidden")
      this.captureBtnTarget.classList.add("hidden")

      this.stop()
    }, "image/jpeg", 0.85)
  }

  // Fires when the Tasker picks a file in the native picker (the fallback
  // path). Shows the chosen photo and Retake, mirroring what capture() does.
  preview() {
    const file = this.inputTarget.files?.[0]
    if (!file) return

    this.revokeResultUrl()
    this.resultTarget.src = URL.createObjectURL(file)
    this.resultTarget.classList.remove("hidden")
    this.retakeBtnTarget.classList.remove("hidden")
    this.triggerTarget.classList.add("hidden")
  }

  retake() {
    this.revokeResultUrl()
    this.resultTarget.classList.add("hidden")
    this.retakeBtnTarget.classList.add("hidden")
    this.triggerTarget.classList.remove("hidden")
    this.inputTarget.value = ""
  }

  revokeResultUrl() {
    const url = this.resultTarget.src
    if (url?.startsWith("blob:")) URL.revokeObjectURL(url)
  }

  stop() {
    this._toggleFocusTap(false)
    this.stream?.getTracks().forEach(t => t.stop())
    this.stream = null
  }

  disconnect() {
    this.stop()
  }

  // ── Tap-to-focus (card #4495) ────────────────────────────────────────────

  // Probe the active track's focus capability and wire up (or leave torn
  // down) the tap listener accordingly. Called right after the stream
  // starts playing, once the track is actually live.
  _syncFocusTap() {
    const track = this.stream?.getVideoTracks?.()[0] || null
    this._toggleFocusTap(!!track)
    if (track) logEvent("camera", "Focus capabilities", { ...focusDiagnosticsForTrack(track), surface: "odometer" }).catch(() => {})
  }

  // Wire up (or tear down) the video element's tap listener. Only ever
  // attached whenever a track is live. Without manual-focus support (iOS,
  // some Android) the tap falls back to the ring plus a best-effort
  // continuous-autofocus nudge.
  _toggleFocusTap(enabled) {
    if (!this.hasVideoTarget) return
    if (enabled && !this._focusTapHandler) {
      this._focusTapHandler = (event) => this._onVideoTap(event)
      this.videoTarget.addEventListener("click", this._focusTapHandler)
    } else if (!enabled && this._focusTapHandler) {
      this.videoTarget.removeEventListener("click", this._focusTapHandler)
      this._focusTapHandler = null
    }
  }

  // Tap handler: translate the tap into a focus point, apply it to the live
  // track, and show the shared focus-ring indicator at the tap location.
  // Best-effort -- a failed applyConstraints still shows the ring (the
  // tasker tapped a real point; whether the hardware honoured it is a
  // hardware concern, not a UI one) but is logged either way.
  _onVideoTap(event) {
    const track = this.stream?.getVideoTracks?.()[0] || null
    if (!track) return
    const point = focusPointFromTap(this.videoTarget, event.clientX, event.clientY)
    if (!point) return

    showFocusRing(this.hasGuideTarget ? this.guideTarget : this.videoTarget.parentElement, event.clientX, event.clientY)
    focusOrNudgeTrack(track, point.x, point.y).then(({ mode, success, confirmed, attempts }) => {
      logEvent("camera", "Tap-to-focus applied", { x: point.x, y: point.y, success, mode, confirmed, attempts, surface: "odometer" }).catch(() => {})
    })
  }
}
